<template>
  <div>
    <!-- PayPal Modal -->
    <div v-show="showPayPal" class="paypal-overlay">
      <div class="paypal-modal">
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          class="close-button"
          @click="closeModal"
          :aria-label="$t('pricing.paypal.close')"
        />
        
        <div class="text-center mb-6">
          <h3 class="text-h5 font-weight-bold mb-2">{{ modalCopy.title }}</h3>
          <p class="text-subtitle-1 mb-2">
            {{ modalCopy.subtitle }}
          </p>
          <p class="text-body-2 text-medium-emphasis">
            {{ modalCopy.description }}
          </p>
        </div>

        <div ref="paypalButtonsContainer"></div>
      </div>
    </div>

    <!-- Notification Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      location="top"
      class="text-center"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { PayPalService } from '@/utils/paypal'
import { trackPaypalOpened, trackPurchase } from '@/utils/analytics'
import { getIdToken } from 'firebase/auth'
import { buildUnlockOffer } from '@/utils/unlockOffer.mjs'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  source: {
    type: String,
    default: 'premium_features'
  },
  context: {
    type: String,
    default: 'default'
  },
  userIntent: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'payment-success', 'payment-error'])

const { t, locale } = useI18n()
const authStore = useAuthStore()
const paypalButtonsContainer = ref(null)
const pricingConfig = ref(null)
const paypalService = new PayPalService(authStore)

const showPayPal = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const analysisUnlockOffer = computed(() =>
  buildUnlockOffer(props.userIntent, {
    genericHeadline: t('analysis.unlock.title'),
    headlineTemplate: (purpose) =>
      t('analysis.unlock.personalizedTitle', { purpose }),
    genericReason: t('analysis.unlock.description'),
    reasonTemplate: (expectations) =>
      t('analysis.unlock.intentReason', { expectations }),
    includedLabel: t('analysis.unlock.includedLabel'),
    purposeLabels: {
      personal: t('analysis.unlock.purposes.personal'),
      professional: t('analysis.unlock.purposes.professional'),
      client: t('analysis.unlock.purposes.client'),
      other: t('analysis.unlock.purposes.other'),
    },
  })
)

const modalCopy = computed(() => {
  const price = formatPrice(pricingConfig.value?.discountedPrice)
  const currency = pricingConfig.value?.currency
  const credits = pricingConfig.value?.credits

  if (props.context === 'analysis_unlock') {
    return {
      title: t('analysis.unlock.payment.title'),
      subtitle: analysisUnlockOffer.value.primaryReason,
      description: t('analysis.unlock.payment.description', {
        credits,
        price,
        currency,
      }),
    }
  }

  return {
    title: t('pricing.paypal.title'),
    subtitle: t('pricing.paypal.subtitle', { credits }),
    description: t('pricing.paypal.description', {
      price,
      currency,
    }),
  }
})

const snackbar = ref({
  show: false,
  text: '',
  color: 'success'
})

const fetchPricingConfig = async () => {
  let headers = {};
  if (authStore.isAuthenticated) {
    const token = await getIdToken(authStore.user);
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`/api/pricing?locale=${locale.value}`, { headers });
  if (!response.ok) {
    throw new Error(`Pricing fetch failed: ${response.status}`);
  }

  const nextPricingConfig = await response.json();
  if (
    typeof nextPricingConfig.credits !== 'number' ||
    nextPricingConfig.credits <= 0 ||
    typeof nextPricingConfig.discountedPrice !== 'number' ||
    typeof nextPricingConfig.regularPrice !== 'number' ||
    !nextPricingConfig.currency
  ) {
    throw new Error('Invalid pricing data received');
  }

  pricingConfig.value = nextPricingConfig;
  return nextPricingConfig;
}

const initializePayPal = async () => {
  try {
    // Clear the container first
    if (paypalButtonsContainer.value) {
      paypalButtonsContainer.value.innerHTML = '';
    }

    const nextPricingConfig = await fetchPricingConfig();
    await nextTick();

    await paypalService.initializePayPalButtons(paypalButtonsContainer.value, {
      onApproveCallback: (result) => {        
        // Fire Google Analytics purchase event
        if (pricingConfig.value && result.orderData) {
          trackPurchase({
            transaction_id: result.orderData.id,
            value: pricingConfig.value.discountedPrice,
            currency: pricingConfig.value.currency,
            items: [
              {
                item_id: `credits_${pricingConfig.value.credits}`,
                item_name: `${pricingConfig.value.credits} Credits`,
                price: pricingConfig.value.discountedPrice,
                quantity: 1
              }
            ]
          })
        }

        showPayPal.value = false
        showNotification(t('pricing.payment.success'), 'success')
        emit('payment-success', result)
      },
      onErrorCallback: (error) => {
        console.error('PayPal error:', error)
        showNotification(t('pricing.payment.error'), 'error')
        emit('payment-error', error)
      },
      locale: locale.value,
      currency: nextPricingConfig.currency
    })
  } catch (error) {
    console.error('Failed to initialize PayPal:', error)
    paypalService.cancelInitialization()
    showPayPal.value = false
    showNotification(t('pricing.payment.error'), 'error')
  }
}

const closeModal = () => {
  showPayPal.value = false
}

const showNotification = (text, color = 'success') => {
  snackbar.value = {
    show: true,
    text,
    color
  }
}

const formatPrice = (price) => {
  if (!price) return '';
  return price.toFixed(2).replace('.', ',');
};

// Track when the modal was last closed so a rapid close+reopen waits for
// the PayPal SDK teardown to settle before starting a new initialisation.
let _lastCloseTime = 0;
const REOPEN_COOLDOWN_MS = 200;

watch(() => showPayPal.value, async (newValue) => {
  if (!newValue) {
    _lastCloseTime = Date.now();
    paypalService.cancelInitialization();

    if (paypalButtonsContainer.value) {
      paypalButtonsContainer.value.innerHTML = '';
    }

    return;
  }

  // On rapid close→reopen (within 200 ms) wait out the cooldown so the
  // previous zoid teardown finishes before we start a new render.  For a
  // normal first-open _lastCloseTime is 0 so the delay is always 0.
  trackPaypalOpened({
    source: props.source,
    isAuthenticated: authStore.isAuthenticated,
  })

  const elapsed = Date.now() - _lastCloseTime;
  if (elapsed < REOPEN_COOLDOWN_MS) {
    await new Promise(resolve => setTimeout(resolve, REOPEN_COOLDOWN_MS - elapsed));
  }

  try {
    await initializePayPal();
  } catch (error) {
    console.error('Failed to open PayPal modal:', error);
    showPayPal.value = false;
    showNotification(t('pricing.payment.error'), 'error');
  }
});

watch(
  () => authStore.user,
  async (user) => {
      if (!user && showPayPal.value) {
        paypalService.cancelInitialization();
        showPayPal.value = false;
        showNotification(t('pricing.payment.error'), 'error');
        return;
      }

      try {
        await fetchPricingConfig();
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
      }
    
  }
);

// Add cleanup when component is unmounted
onUnmounted(() => {
  paypalService.cancelInitialization();
});
</script>

<style scoped>
.paypal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.paypal-modal {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 90%;
  position: relative;
}

.close-button {
  position: absolute;
  top: 8px;
  right: 8px;
}
</style> 
